/* eslint-disable react/display-name */
import React, { useEffect, useState } from "react";
import { Field } from "@atlaskit/form";
import Modal, { ModalTransition, ModalFooter } from "@atlaskit/modal-dialog";
import TextField from "@atlaskit/textfield";
import ButtonGroup from "@atlaskit/button/button-group";
import Button from "@atlaskit/button/custom-theme-button";
import useAsync from "../../hooks/use-async";
import apiClient from "../../api/admin-api-client";
import SectionMessage from "@atlaskit/section-message";
import styled from "styled-components";
import { toast } from "react-toastify";
import TextArea from "@atlaskit/textarea";
import { useTranslation } from "react-i18next";
import { JsonEditor as Editor } from "jsoneditor-react";
import "jsoneditor-react/es/editor.min.css";
import Ajv from "ajv";

export default function EditModule({ module, onClose, onSaved }) {
    const { t } = useTranslation();
    const { run, isPending, error, isError } = useAsync();
    const [name, setName] = useState(module.name);
    const [alias, setAlias] = useState(module.alias);
    const [icon, setIcon] = useState(module.icon);
    const [position, setPosition] = useState(module?.position ?? 0);
    const [config, setConfig] = useState(() => {
        try {
            return JSON.stringify(module.config) ?? "";
        } catch (e) {
            return "";
        }
    });

    const [parsedConfig, setParsedConfig] = useState(() => {
        try {
            return JSON.parse(config);
        } catch (e) {
            return "";
        }
    });

    useEffect(() => {
        try {
            setParsedConfig(JSON.parse(config));
        } catch (e) {
            console.log(e);
        }
    }, [config]);

    const footer = (props) => (
        <ModalFooter showKeyline={props.showKeyline}>
            <ButtonGroup>
                <Button appearance="primary" type="button" isLoading={isPending} onClick={handleSubmit}>
                    {t("admin_edit_module_save")}
                </Button>
                <Button type="button" isDisabled={isPending} onClick={onClose}>
                    {t("admin_edit_module_cancel")}
                </Button>
            </ButtonGroup >
        </ModalFooter>
    );

    const handleSubmit = () => {
        const formData = {
            name,
            alias,
            position,
            icon,
            config: JSON.parse(config),
        };

        run(apiClient(`modules/${module.id}`, { method: "PATCH", data: formData }))
            .then(response => {
                onSaved(response);
                onClose();
                toast.success(t("admin_edit_module_success"));
            })
            .catch(() => {
                toast.error(t("admin_edit_module_error"));
            });
    };

    return <ModalTransition>
        <Modal
            onClose={onClose}
            heading={t("admin_edit_module_heading")}
            scrollBehavior="inside-wide"
            components={{
                Footer: footer,
            }}
        >
            {isError && <SectionMessageWrapper>
                <SectionMessage title={t("admin_edit_module_error_heading")} appearance="error">
                    <p>{error?.message}</p>
                    {error?.errors && <ul>
                        {Object.keys(error.errors).map(key => (
                            <li key={key}>{error.errors[key]}</li>
                        ))}
                    </ul>}
                </SectionMessage>
            </SectionMessageWrapper>}
            <Field name="name" label={t("admin_edit_module_name")} isRequired autoFocus isDisabled={isPending}>
                {({ fieldProps }) => <TextField {...fieldProps} value={name} onChange={e => setName(e.target.value)} isInvalid={error?.errors?.name} autoComplete={false} />}
            </Field>
            <Field name="alias" label={t("admin_edit_module_alias")} isRequired autoFocus isDisabled={isPending}>
                {({ fieldProps }) => <TextField {...fieldProps} value={alias} onChange={e => setAlias(e.target.value)} isInvalid={error?.errors?.alias} autoComplete={false} />}
            </Field>
            {/* <Field name="config" label={t("admin_edit_module_config")} isRequired autoFocus isDisabled={isPending}>
                {({ fieldProps }) => <TextArea
                    {...fieldProps}
                    isMonospaced
                    value={config}
                    onChange={e => setConfig(e?.target?.value)}
                    minimumRows={5}
                />}
            </Field> */}
            <Field name="config" label={t("admin_edit_module_config")} isRequired autoFocus isDisabled={isPending}>
                {({ fieldProps }) => <Editor
                    value={parsedConfig}

                    mode={Editor.modes.code}
                    theme="ace/theme/tomorrow_night_blue"
                    ajv={Ajv({ allErrors: true, verbose: true })}
                    onChange={e => {
                        try {
                            setConfig(JSON.stringify(e));
                        } catch (e) {
                            console.log(e);
                        }
                    }}
                />}
            </Field>
            <Field name="position" label={t("create_module_position")} isRequired isDisabled={isPending}>
                {({ fieldProps }) => <TextField {...fieldProps} value={position} onChange={e => setPosition(e.target.value)} isInvalid={error?.errors?.position} autoComplete={false} />}
            </Field>
            <Field name="icon" label={t("create_module_icon")} autoFocus isDisabled={isPending}>
                {({ fieldProps }) => <TextField {...fieldProps} value={icon} onChange={e => setIcon(e.target.value)} isInvalid={error?.errors?.icon} autoComplete={false} />}
            </Field>
        </Modal>
    </ModalTransition>;
}

const SectionMessageWrapper = styled.div`
    margin-top: 0px;
    font-size: 13px;
`;
